/* eslint-disable import/no-extraneous-dependencies */
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
// eslint-disable-next-line css-modules/no-unused-class
import s from './FlipCardAd.scss';
import {
  displayScorecardAd,
  checkJarvisAdActiveAndGetConfig,
} from '../ScorecardWithAd/CriAdWidget';

const FlipCardAd = props => {
  // eslint-disable-next-line react/prop-types
  const {
    children,
    width,
    height,
    skipMwebAdSize,
    isFlipActive,
    isPrime,
    isMobile,
    msid,
    flipTrigger,
    campaignAdConfig,
  } = props;
  const [flipped, setflipped] = useState(false);
  const [adConfig, setAdsConfig] = useState(null);
  const [inView, setInView] = useState(false);
  const [maxFlipped, setMaxFlipped] = useState(0);
  const prevFlipTriggerRef = useRef(flipTrigger);

  const parentRef = useRef(null);
  const timer1Ref = useRef(null);
  const timer2Ref = useRef(null);
  const observerRef = useRef(null);

  const clearTimer = useCallback(timerRef => {
    if (timerRef?.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const adCall = useCallback(
    () => {
      if (!adConfig) {
        return;
      }

      const {
        adTimeout = 7,
        displayTimout = 1000,
        nextAdTimout = 10,
      } = adConfig;
      displayScorecardAd(
        r => {
          if (r) {
            clearTimer(timer1Ref);
            timer1Ref.current = setTimeout(() => {
              setflipped(prev => !prev);
            }, displayTimout);
          } else {
            const nextt = nextAdTimout * 1000;
            clearTimer(timer2Ref);
            timer2Ref.current = setTimeout(() => {
              setMaxFlipped(prev => prev + 1);
            }, nextt);
            setflipped(false);
          }
        },
        adTimeout,
        adConfig,
      );
    },
    [adConfig, clearTimer],
  );

  useEffect(
    () => {
      if (!adConfig || flipTrigger !== undefined) {
        return undefined;
      }

      const maxAd = adConfig.maxads || 50;
      if (inView && maxFlipped < maxAd && maxFlipped > 0) {
        adCall();
      }

      return () => {
        clearTimer(timer1Ref);
        clearTimer(timer2Ref);
      };
    },
    [maxFlipped, inView, adConfig, adCall, clearTimer, flipTrigger],
  );

  useEffect(
    () => {
      if (!adConfig || flipTrigger !== undefined) {
        return undefined;
      }

      const node = parentRef?.current;
      if (!(node instanceof HTMLElement)) {
        return undefined;
      }

      observerRef.current = new IntersectionObserver(
        entries => {
          const [entry] = entries;
          const { isIntersecting } = entry;
          setInView(isIntersecting);
          if (isIntersecting) {
            setMaxFlipped(prev => prev + 1);
            // Stop observing after first intersection
            if (observerRef.current && node instanceof HTMLElement) {
              observerRef.current.unobserve(node);
            }
          }
        },
        { rootMargin: '0px' },
      );

      observerRef.current.observe(node);

      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect();
          observerRef.current = null;
        }
      };
    },
    [adConfig, flipTrigger],
  );

  useEffect(
    () => {
      let flipadconf = checkJarvisAdActiveAndGetConfig('scorecard_flip_ad');
      if (window.location.href.indexOf('test2=noflipad') > -1) {
        flipadconf = null;
      }
      if (!flipadconf || flipTrigger !== undefined) {
        return undefined;
      }

      let processedConfig = flipadconf;
      if (isMobile) {
        if (skipMwebAdSize) {
          processedConfig = { ...flipadconf, adCode: flipadconf?.mweb?.adCode };
        } else {
          processedConfig = { ...flipadconf, ...flipadconf?.mweb };
        }
      }

      const adObj = {
        ...processedConfig,
        divId: `${processedConfig.divId}-${msid}`,
      };
      const startAfer = processedConfig.startAfer || 1;
      const timer3 = setTimeout(() => {
        setAdsConfig(adObj);
      }, startAfer * 1000);

      return () => {
        clearTimeout(timer3);
      };
    },
    [isMobile, skipMwebAdSize, msid, flipTrigger],
  );

  // Handle external flip trigger based on input prop
  useEffect(
    () => {
      // Only flip if flipTrigger is provided and has changed
      if (
        flipTrigger !== undefined &&
        flipTrigger !== prevFlipTriggerRef.current
      ) {
        // If flipTrigger is boolean, set flipped state directly
        if (typeof flipTrigger === 'boolean') {
          setflipped(flipTrigger);
        } else {
          setflipped(prev => !prev);
        }
        prevFlipTriggerRef.current = flipTrigger;
      }
    },
    [flipTrigger],
  );

  // Memoize computed values - MUST be called before any conditional returns
  // Apply height/width only if flipTrigger is undefined, otherwise let it adapt to parent
  const cardStyle = useMemo(
    () => {
      // if (flipTrigger !== undefined) {
      //   return {};
      // }
      return { height, width };
    },
    [height, width, flipTrigger],
  );

  const containerClassName = useMemo(
    () => {
      const classes = [s.card];
      if (isMobile) {
        classes.push(s.upsidedown);
      }
      if (flipped) {
        classes.push(s.flipped);
      }
      return classes.join(' ');
    },
    [flipped, isMobile],
  );

  const handleCloseAd = useCallback(() => {
    setflipped(false);
  }, []);

  // Early return optimization - check conditions after all hooks
  // Allow rendering if campaignAdConfig is provided via flipTrigger
  const hasAdConfig =
    adConfig || (flipTrigger !== undefined && campaignAdConfig);
  if (!hasAdConfig || !isFlipActive || isPrime) {
    return (
      <div className={s.wrapper} ref={parentRef}>
        {children}
      </div>
    );
  }

  return (
    <div className={containerClassName} style={cardStyle}>
      <div className={s.card_inner}>
        <div className={`${s.card_face} ${s.card_front}`}>
          <div className={s.wrapper} ref={parentRef}>
            {children}
          </div>
        </div>
        <div className={`${s.card_face} ${s.card_back}`}>
          <div className={s.close_ad} onClick={handleCloseAd} />
          <div
            id={adConfig?.divId || campaignAdConfig?.divId}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: '' }}
            suppressHydrationWarning
            className={flipped ? '' : s.hideAd}
          />
        </div>
      </div>
    </div>
  );
};

FlipCardAd.propTypes = {
  height: PropTypes.string,
  width: PropTypes.string,
  skipMwebAdSize: PropTypes.bool,
  isFlipActive: PropTypes.bool,
  isPrime: PropTypes.bool,
  children: PropTypes.node,
  isMobile: PropTypes.bool,
  msid: PropTypes.string,
  flipTrigger: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.number,
    PropTypes.string,
  ]),
  campaignAdConfig: PropTypes.shape({
    divId: PropTypes.string,
    adCode: PropTypes.string,
    adTimeout: PropTypes.number,
    displayTimout: PropTypes.number,
    nextAdTimout: PropTypes.number,
  }),
};
FlipCardAd.defaultProps = {
  width: '300px',
  height: '114px',
  skipMwebAdSize: false,
  isFlipActive: false,
  isPrime: false,
  children: null,
  isMobile: false,
  msid: '',
  flipTrigger: undefined,
  campaignAdConfig: null,
};
const mapStateToProps = state => ({
  isPrime: state.isPrime,
  isMobile: state.isMobile,
});
export default connect(mapStateToProps)(FlipCardAd);
